/**
 * The pure core of the header's scheme toggle (FR-019(b), T042).
 *
 * One control, three modes. That shape is deliberate: a three-way segmented
 * control would be the wider, louder thing in a header that carries nothing
 * else, and it would have to be squeezed at the 320 CSS px viewport FR-019(f)
 * holds the page to. A single control also means the state and the action can
 * be said in one accessible name instead of spread across three.
 *
 * Both answers the control needs — where a press goes, and what the control
 * calls itself while it waits — are decided here rather than inside the
 * component, so they can be unit-tested without loading Paper under jsdom
 * (research D13). `theme-provider.tsx` owns everything else: persistence, the
 * OS subscription, and the resolution to a renderable scheme.
 */

import type { ThemeMode } from "./resolve-scheme";

/**
 * `system` first, because that is the default and the mode a visitor starts
 * in; the two explicit choices follow, and the cycle closes back to letting
 * the device decide. Every mode is on the ring, so each is reachable from any
 * other in at most two presses.
 */
const CYCLE: Record<ThemeMode, ThemeMode> = {
  system: "light",
  light: "dark",
  dark: "system",
};

/** The mode a press of the toggle selects. */
export function nextThemeMode(mode: ThemeMode): ThemeMode {
  return CYCLE[mode];
}

/**
 * The toggle's accessible name — its only name, since it shows a glyph and no
 * text. FR-019(b) asks it to state its purpose and its current state, and a
 * cycling control needs one thing more: where the next press lands. Without
 * that, someone who cannot see the glyph change has to press and re-listen to
 * find out what they just did.
 */
export function schemeToggleLabel(mode: ThemeMode): string {
  return `Appearance: ${mode}. Switch to ${nextThemeMode(mode)}.`;
}
